import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import CreateLogComponent from './CreateLog';
import './ReminderLog.css';
import { Minus, Plus } from './icons';
import type { MessageLog } from '@/data/logs';
import type { Reminder } from '@/data/reminders';
import { $currentOperator, $defaultFrequency, $myCallsign } from '@/stores';
import { logs } from '@/stores/logbook';
import { alert } from '@/utils/alertUtils';
import { parseCallSign, parseTime } from '@/utils/dataUtils';
import { useStore } from '@nanostores/react';
import { useCallback, useState } from 'react';

export default function ReminderLogComponent({
  reminder,
  onDismiss,
}: {
  reminder: Reminder;
  onDismiss: () => void;
}) {
  const operator = useStore($currentOperator);
  const myCallsign = useStore($myCallsign);
  const defaultFrequency = useStore($defaultFrequency);

  const [frequency, setFrequency] = useState(reminder.frequency);
  const [callSign, setCallSign] = useState(
    `${reminder.callSign.from} > ${reminder.callSign.to}`,
  );
  const [description, setDescription] = useState('');
  const [time, setTime] = useState('');
  const [tagIds, setTagIds] = useState<string[]>(reminder.tags || []);

  const save = useCallback(() => {
    const newTime = parseTime(time);

    if (newTime === null) {
      alert({
        title: 'Error',
        message: 'Invalid time format',
      });
      return;
    }

    const parsedCallSign = parseCallSign(callSign, myCallsign);
    if (!parsedCallSign) {
      alert({
        title: 'Error',
        message: 'Invalid call sign format',
      });
      return;
    }

    const log: MessageLog = {
      id: `log-${Date.now()}`,
      type: 'message',
      draft: false,
      tags: tagIds,
      entries: [
        {
          timestamp: Date.now(),
          frequency: frequency || defaultFrequency,
          callSign: parsedCallSign,
          time: newTime,
          description: description || reminder.description,
          operator,
        },
      ],
    };

    logs.addLog(log);

    onDismiss();
  }, [
    time,
    callSign,
    myCallsign,
    tagIds,
    frequency,
    defaultFrequency,
    description,
    reminder.description,
    operator,
    onDismiss,
  ]);

  return (
    <CreateLogComponent
      className="reminder-flash border-y-2 bg-red-100"
      frequency={frequency}
      setFrequency={setFrequency}
      callSign={callSign}
      setCallSign={setCallSign}
      description={description}
      setDescription={setDescription}
      descriptionPlaceholder={reminder.reminder}
      time={time}
      setTime={setTime}
      tagIds={tagIds}
      setTagIds={setTagIds}
      save={save}
      saveButton={
        <div className="flex h-full items-center justify-center">
          <button
            id="new-log-save"
            onClick={save}
            className="h-full cursor-pointer rounded-l bg-green-500 px-1 py-2 text-white hover:bg-green-600"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            id="new-log-dismiss"
            onClick={onDismiss}
            className="h-full cursor-pointer rounded-r bg-red-500 px-1 py-2 text-white hover:bg-red-600"
          >
            <Minus className="h-4 w-4" />
          </button>
        </div>
      }
    />
  );
}
